'use strict'
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'

// ── Auto-Optimize: global VROOM reassignment run (server-side, async) ──
// POST kicks off a run; we poll /auto-optimize/status until it leaves
// 'running', then announce the summary and refetch the analysis.
//
// Extracted from RefineRouteTab.js (Task 8) so the same run lifecycle can
// be mounted on other tabs (e.g. the Map tab, Task 9). Lifted verbatim
// except: fetchAnalysis(true) -> onApplied(), anchorDate -> date,
// selectedScenarioId -> scenarioId.
export default function useAutoOptimize ({ url, scenarioId, date, onSnackbar, onApplied }) {
  const [autoRun, setAutoRun] = useState(null) // latest run from /auto-optimize/status
  const autoRunActive = autoRun?.status === 'running'
  const autoRunWasRunningRef = useRef(false)

  const fetchAutoOptimizeStatus = useCallback(async () => {
    if (!scenarioId) return null
    try {
      const res = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/auto-optimize/status`)
      return res.data?.run || null
    } catch (err) {
      return null
    }
  }, [scenarioId, url])

  // Reattach on mount / scenario change: adopt an in-flight run (resumes
  // polling) or the latest finished run (enables the Report button).
  useEffect(() => {
    let stale = false
    setAutoRun(null)
    autoRunWasRunningRef.current = false
    fetchAutoOptimizeStatus().then(run => {
      if (stale || !run) return
      autoRunWasRunningRef.current = run.status === 'running'
      setAutoRun(run)
    })
    return () => { stale = true }
  }, [fetchAutoOptimizeStatus])

  // Run report drawer: summary from the run doc + this run's move-log entries
  const [runReportOpen, setRunReportOpen] = useState(false)
  const [runMoves, setRunMoves] = useState([])
  const [runMovesLoading, setRunMovesLoading] = useState(false)
  const openRunReport = useCallback(async (run) => {
    setRunReportOpen(true)
    if (!scenarioId || !run?.runId) { setRunMoves([]); return }
    setRunMovesLoading(true)
    try {
      const res = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/move-logs`, {
        params: { optimizeRunId: run.runId, limit: 500 }
      })
      setRunMoves(res.data?.logs || res.data?.moveLogs || [])
    } catch (err) {
      setRunMoves([])
    } finally {
      setRunMovesLoading(false)
    }
  }, [scenarioId, url])

  useEffect(() => {
    if (!autoRunActive) return undefined
    const timer = setInterval(async () => {
      const run = await fetchAutoOptimizeStatus()
      if (!run) return
      setAutoRun(run)
      if (autoRunWasRunningRef.current && run.status !== 'running') {
        autoRunWasRunningRef.current = false
        if (run.status === 'completed') {
          const s = run.summary || {}
          const warningCount = (s.unassigned?.length || 0) + (s.overflowWarnings?.length || 0) + (s.applyWarnings?.length || 0)
          onSnackbar(
            `Auto-optimize: moved ${s.movedCount ?? 0} · drive ${s.driveMinBefore ?? '?'}→${s.driveMinAfter ?? '?'} min/fortnight` +
            `${Number(s.otMinBefore) > 0 || Number(s.otMinAfter) > 0 ? ` · OT ${s.otMinBefore ?? 0}→${s.otMinAfter ?? 0} min` : ''}` +
            `${warningCount ? ` · ${warningCount} warning${warningCount === 1 ? '' : 's'}` : ''}`,
            warningCount ? 'info' : 'success'
          )
          onApplied()
          openRunReport(run)
        } else if (run.status === 'failed') {
          onSnackbar(`Auto-optimize failed: ${run.error || 'unknown error'}`, 'error')
        } else if (run.status === 'cancelled') {
          onSnackbar('Auto-optimize cancelled', 'info')
        }
      }
    }, 2500)
    return () => clearInterval(timer)
  }, [autoRunActive, fetchAutoOptimizeStatus, onSnackbar, onApplied, openRunReport])

  const [autoOptMenuAnchor, setAutoOptMenuAnchor] = useState(null)
  // Opt-in: also feed unscheduled-tray properties into the solve as free jobs
  const [placeUnassigned, setPlaceUnassigned] = useState(false)
  const startAutoOptimize = useCallback(async (mode = 'balanced') => {
    if (!scenarioId) return
    try {
      const res = await axios.post(`${url}/api/hmi/territory/scenarios/${scenarioId}/auto-optimize`, {
        date: date || null,
        mode,
        placeUnassigned
      })
      autoRunWasRunningRef.current = true
      setAutoRun({ runId: res.data?.runId, status: 'running', phase: 'loading', progress: null })
      onSnackbar('Auto-optimize started — this can take a few minutes', 'info')
    } catch (err) {
      onSnackbar(err.response?.data?.message || err.message || 'Failed to start auto-optimize', 'error')
    }
  }, [scenarioId, url, date, onSnackbar, placeUnassigned])

  // Time audit: over-budgeted stops on OT crew-days (prescribed vs actual)
  const [timeAudit, setTimeAudit] = useState({ open: false, loading: false, suggestions: [], meta: null })
  const [auditApplyingIds, setAuditApplyingIds] = useState({})
  const openTimeAudit = useCallback(async () => {
    if (!scenarioId) return
    setTimeAudit({ open: true, loading: true, suggestions: [], meta: null })
    try {
      const res = await axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/time-audit`, {
        params: date ? { date } : {}
      })
      setTimeAudit({ open: true, loading: false, suggestions: res.data?.suggestions || [], meta: res.data?.meta || null })
    } catch (err) {
      setTimeAudit({ open: true, loading: false, suggestions: [], meta: null })
      onSnackbar(err.response?.data?.message || err.message || 'Failed to build time audit', 'error')
    }
  }, [scenarioId, url, date, onSnackbar])

  const applyTimeAuditRow = useCallback(async (row) => {
    if (!scenarioId || !row?.aspirePropertyId) return false
    setAuditApplyingIds(prev => ({ ...prev, [row.aspirePropertyId]: true }))
    try {
      await axios.patch(
        `${url}/api/hmi/territory/properties/${row.aspirePropertyId}`,
        { prescribedManHours: row.suggestedManHours },
        { params: { scenarioId } }
      )
      onSnackbar(`${row.propertyName}: prescribed ${row.prescribedManHours != null ? row.prescribedManHours : '—'}h → ${row.suggestedManHours}h`, 'success')
      onApplied()
      return true
    } catch (err) {
      onSnackbar(err.response?.data?.message || err.message || 'Failed to update prescribed hours', 'error')
      return false
    } finally {
      setAuditApplyingIds(prev => { const next = { ...prev }; delete next[row.aspirePropertyId]; return next })
    }
  }, [scenarioId, url, onSnackbar, onApplied])

  const cancelAutoOptimize = useCallback(async () => {
    if (!scenarioId) return
    try {
      await axios.post(`${url}/api/hmi/territory/scenarios/${scenarioId}/auto-optimize/cancel`)
      onSnackbar('Cancelling auto-optimize…', 'info')
    } catch (err) {
      onSnackbar(err.response?.data?.message || err.message || 'Failed to cancel', 'error')
    }
  }, [scenarioId, url, onSnackbar])

  return {
    autoRun,
    autoRunActive,
    autoOptMenuAnchor,
    setAutoOptMenuAnchor,
    placeUnassigned,
    setPlaceUnassigned,
    startAutoOptimize,
    cancelAutoOptimize,
    runReportOpen,
    setRunReportOpen,
    runMoves,
    runMovesLoading,
    openRunReport,
    timeAudit,
    setTimeAudit,
    openTimeAudit,
    applyTimeAuditRow,
    auditApplyingIds
  }
}
